import { toStoreDateString } from '@/common/helpers/dates'

import { compareLayoutItems } from '../useTrackingPageLayout/helpers'
import { alwaysVisiblePayload, initBlockVisibility } from './constants'

export const hasDateLimit = visibility => {
  return visibility.limitByDate && Boolean(visibility.visible_from || visibility.visible_until)
}

export const hasCriteriaLimit = visibility => {
  return visibility.limitByCriteria && visibility.visibility_criterion_ids.length > 0
}

const getBlockIdsInCanvasOrder = layout => {
  const sortedLayout = [...layout].sort(compareLayoutItems)

  return sortedLayout.map(item => item.i)
}

const isBlockActiveOnDate = ({ visibility, date, timeZone }) => {
  const from = toStoreDateString(visibility.visible_from, timeZone)
  const until = toStoreDateString(visibility.visible_until, timeZone)

  if (from && date < from) return false
  if (until && date > until) return false

  return true
}

export const getDateLimitedBlocks = ({ layout, getBlockVisibility, date, timeZone }) => {
  if (!date || !layout) return []

  const blockIds = getBlockIdsInCanvasOrder(layout)
  const dateLimitedBlockIds = blockIds.filter(blockId => hasDateLimit(getBlockVisibility(blockId)))

  return dateLimitedBlockIds.map(blockId => {
    const visibility = getBlockVisibility(blockId)

    return { blockId, isActive: isBlockActiveOnDate({ visibility, date, timeZone }) }
  })
}

export const getCriterionLabel = criterion => `#${criterion.internal_id} | ${criterion.name}`

export const getCriteriaLimitedBlockIds = (layout, getBlockVisibility) => {
  const blockIds = getBlockIdsInCanvasOrder(layout)

  return blockIds.filter(blockId => hasCriteriaLimit(getBlockVisibility(blockId)))
}

const getCriteriaNames = (criterionIds, criteriaOptions) => {
  const blockCriteria = criteriaOptions.filter(criterion => criterionIds.includes(criterion.id))

  return blockCriteria.map(getCriterionLabel)
}

export const getCriteriaLimitedBlocks = ({ layout, getBlockVisibility, criteriaOptions, isCriterionActive }) => {
  if (!layout) return []

  const criteriaLimitedBlockIds = getCriteriaLimitedBlockIds(layout, getBlockVisibility)

  return criteriaLimitedBlockIds.map(blockId => {
    const criterionIds = getBlockVisibility(blockId).visibility_criterion_ids

    return {
      blockId,
      isActive: criterionIds.some(isCriterionActive),
      criteriaNames: getCriteriaNames(criterionIds, criteriaOptions),
    }
  })
}

const buildBlockVisibilityFromConfig = config => {
  return {
    limitByDate: Boolean(config.visible_from || config.visible_until),
    visible_from: config.visible_from,
    visible_until: config.visible_until,
    limitByCriteria: config.visibility_criterion_ids.length > 0,
    visibility_criterion_ids: config.visibility_criterion_ids,
  }
}

export const buildBlockVisibilityFromSaved = savedBlocks => {
  const entries = savedBlocks.map(block => [block.block_uid, buildBlockVisibilityFromConfig(block)])

  return Object.fromEntries(entries)
}

export const buildAdvancedBlockPayload = (visibility, timeZone) => {
  return {
    visible_from: visibility.limitByDate ? toStoreDateString(visibility.visible_from, timeZone) : null,
    visible_until: visibility.limitByDate ? toStoreDateString(visibility.visible_until, timeZone) : null,
    visibility_criterion_ids: visibility.limitByCriteria ? visibility.visibility_criterion_ids : [],
  }
}

export const isAlwaysVisible = payload => {
  return !payload.visible_from && !payload.visible_until && payload.visibility_criterion_ids.length === 0
}

const getSavedPayload = (savedVisibility, blockUid, timeZone) => {
  const saved = savedVisibility[blockUid]

  if (!saved) return alwaysVisiblePayload

  return buildAdvancedBlockPayload(saved, timeZone)
}

const haveSameCriteria = (criterionIds, savedCriterionIds) => {
  return criterionIds.length === savedCriterionIds.length && criterionIds.every(id => savedCriterionIds.includes(id))
}

const havePayloadsChanged = (payload, savedPayload) => {
  return (
    payload.visible_from !== savedPayload.visible_from ||
    payload.visible_until !== savedPayload.visible_until ||
    !haveSameCriteria(payload.visibility_criterion_ids, savedPayload.visibility_criterion_ids)
  )
}

export const getBlockVisibilitySyncActions = ({ layout, blockVisibility, savedVisibility, timeZone }) => {
  const layoutBlockUids = layout.map(item => item.i)

  const payloads = layoutBlockUids.map(blockUid => {
    return { blockUid, payload: buildAdvancedBlockPayload(blockVisibility[blockUid] || initBlockVisibility, timeZone) }
  })

  const updates = payloads.filter(({ blockUid, payload }) => {
    if (isAlwaysVisible(payload)) return false

    return havePayloadsChanged(payload, getSavedPayload(savedVisibility, blockUid, timeZone))
  })

  const deletions = Object.keys(savedVisibility).filter(blockUid => {
    const current = payloads.find(item => item.blockUid === blockUid)

    return !current || isAlwaysVisible(current.payload)
  })

  return { updates, deletions }
}

export const haveBlockVisibilityChanged = params => {
  const { updates, deletions } = getBlockVisibilitySyncActions(params)

  return updates.length > 0 || deletions.length > 0
}
